import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, forkJoin, Observable, of, Subject, timer, zip } from "rxjs";
import { map, mergeMap, take, tap } from 'rxjs/operators';
import {
    SimpleMessage,
    ToastButtons,
    ToastMessageType,
    ToastResponse,
    ToastService
} from "../../../../../Common/toast";
import { KeyValueString } from "../../../../../Models/KeyValueString";
import {
    MatrixConfiguration,
    MatrixConfigurationViewModel,
    MatrixDefinition,
    MatrixDefinitionViewModel,
    MatrixExportDefinition,
    SimpleTranslation
} from "../../../../../Models/MatrixViewModel";
import { QueryViewModel } from '../../../../../Models/QueryViewModel';
import { TenantService } from "../../../../../Services";
import { TranslationService } from "../../Services/translation.service";
import { MatrixService } from "../matrix.service";
import { NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { LoadingElementsComponent } from '../../../../../Common/loading-elements/loading-elements.component';
import { MatCard } from '@angular/material/card';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { ValidationErrorListComponent } from '../validation-error-list/validation-error-list.component';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { ColorSwatchComponent } from '../../../../../Common/color-swatch/color-swatch.component';
import { MatrixFieldEditFormComponent } from '../matrix-field-edit-form/matrix-field-edit-form.component';
import { TitleEditFormComponent } from '../title-edit-form/title-edit-form.component';
import { TranslationPipe } from '../../../../../Services/translation.pipe';

@Component({
    selector: 'aw-matrix',
    templateUrl: './matrix.component.html',
    styleUrls: ['./matrix.component.less'],
    imports: [LoadingElementsComponent, MatCard, NgClass, ExtendedModule, ValidationErrorListComponent, NgStyle, FormsModule, MatButton, ColorSwatchComponent, MatrixFieldEditFormComponent, TitleEditFormComponent, AsyncPipe, TranslationPipe]
})
export class MatrixComponent implements OnInit {
    configurationTitle: string;
    definitionTitle: string;
    configurationHeader: MatrixConfiguration[] = [];
    configurationBody: MatrixConfiguration[][] = [];
    definitionHeader: MatrixDefinition[] = [];
    definitionBody: MatrixDefinition[][] = [];
    scoreAndColour: boolean = false;
    savedColour: string;
    currentModel = null;
    selectedCell;
    editMode: boolean = false;
    fileToUpload: any = null;
    queryViewModel: QueryViewModel;
    matrixSeverityFields: string[] = [];
    editingTitle: KeyValueString = null;
    blockUi: boolean = false;
    loaded: boolean = false;
    availableColours: any[] = [];
    matrixName: string;
    newMatrixName: string;
    isCopy: boolean = false;
    isSeverityMatrix: boolean = false;
    readonly newItem: string = "New";
    readonly textProperty: string = 'Text';
    readonly definitionTitleAlias: string = 'DefinitionDescription';
    readonly configurationTitleAlias: string = 'ConfigurationDescription';
    readonly sevMatrixKey: string = 'sevmatrix';
    readonly sevMatrixDefaultKey: string = 'sevmatrix_default';
    readonly newItemColour: string = '#3692e7';

    constructor(
        private translationService: TranslationService,
        private matrixService: MatrixService,
        private toast: ToastService,
        private tenantService: TenantService,
        private route: ActivatedRoute
    ) { }

    ngOnInit() {
        let timerSubscription = timer(500).subscribe(() => this.blockUi = true);

        this.matrixName = this.route.snapshot.params["name"];
        this.isCopy = this.route.snapshot.url.map(u => u.path === "copy")[2];
        if (!this.isCopy)
            this.newMatrixName = this.matrixName;
        this.isSeverityMatrix = this.newMatrixName === 'SeverityMatrix';

        zip(
            this.matrixService.getMatrix(this.matrixName)
                .pipe(map(result => {
                    this.applyToModel({
                        configurationBody: result.configurationBody,
                        configurationHeader: result.configurationHeader,
                        definitionHeader: result.definitionHeader,
                        definitionBody: result.definitionBody
                    })
                })),
            this.matrixService.getSeverityMatrixQuery()
                .pipe(
                    map(result => {
                        this.queryViewModel = result;
                        this.matrixSeverityFields = this.matrixService.getMatrixSeverityDefinitionFields(this.queryViewModel);
                    })
                )
        ).subscribe(() => {
            for (let row of this.configurationBody) {
                for (let col of row) {
                    col.IncludedInQuery = this.matrixSeverityFields.filter(a => a === col.Alias).length > 0;
                }
            }
            timerSubscription.unsubscribe();
            this.loaded = true;
            this.blockUi = false
        });
    }

    toggleEditMode() {
        this.editMode = !this.editMode;
    }

    spreadColour(col: MatrixConfiguration) {
        col.Colour = this.savedColour;
        this.refreshAvailableColours();
        return false;
    }

    insertConfigurationColumn(index) {
        this.configurationBody.forEach(entry => {
            let item: MatrixConfiguration = {
                Alias: this.newItem,
                Score: null,
                Colour: this.newItemColour,
                IncludedInQuery: false
            };
            entry.splice(index, 0, item);
        });
        this.configurationHeader.splice(index, 0, { Alias: this.newItem, Colour: null, Score: null, IncludedInQuery: false })
    }

    deleteConfigurationColumn(index) {
        this.configurationHeader.splice(index, 1);
        this.configurationBody.forEach(entry => {
            entry.splice(index, 1);
        });
    }

    insertDefinitionColumn(index) {
        this.definitionBody.forEach(entry => {
            let item: MatrixDefinition = {
                Alias: this.newItem
            };
            entry.splice(index, 0, item);
        });
        let header: MatrixDefinition = {
            Alias: this.newItem
        };
        this.definitionHeader.splice(index, 0, header);
    }

    deleteDefinitionColumn(index) {
        this.definitionBody.forEach(entry => {
            entry.splice(index, 1);
        });
        this.definitionHeader.splice(index, 1);
    }

    insertRow(index) {
        let items = [];
        let itemCount = this.definitionBody[0].length;
        while (itemCount > 0) {
            items.push({
                Alias: this.newItem,
            });
            itemCount--;
        }

        this.definitionBody.splice(index, 0, items);

        let configItems = [];
        let configCount = this.configurationBody[0].length;
        while (configCount > 0) {
            configItems.push({
                Alias: this.newItem,
                Score: null,
                Colour: this.newItemColour
            });
            configCount--;
        }
        this.configurationBody.splice(index, 0, configItems);
    }

    deleteRow(index) {
        this.definitionBody.splice(index, 1);
        this.configurationBody.splice(index, 1);
    }

    saved($event) {
        this.currentModel = { ...this.currentModel, $event };
        this.cancelEditing();
        this.matrixService.validateData(this.convertCurrentToExportDefinition());
        this.refreshAvailableColours();
    }

    validate(showSuccess: boolean = true) {
        const invalidCharsRegex = /[/?#&%]/;
        if (invalidCharsRegex.test(this.newMatrixName)) {
            this.toast.sendMessage(
              'Invalid Name',
              'Matrix name contains unsupported characters like / ? # & %. Please remove them.',
              ToastMessageType.error
            );
            return false;
        }
        if (!this.matrixService.validateData(this.convertCurrentToExportDefinition())) {
            if (showSuccess) {
                this.toast.sendMessage('Validation Successful', 'Looks good!', ToastMessageType.success);
            }
            return true;
        } else {
            this.toast.sendMessage('Errors', 'Looks like there are some validation errors', ToastMessageType.error);
            return false;
        }
    }

    import(files: FileList) {
        this.fileToUpload = files.item(0) as any;
        this.fileToUpload.text().then((result: string) => {
            let data: MatrixExportDefinition = JSON.parse(result);
            this.applyToModel(data);
        });
    }

    applyToModel(data: MatrixExportDefinition) {
        this.configurationTitle = data.configurationTitle;
        this.definitionTitle = data.definitionTitle;
        this.configurationHeader = data.configurationHeader;
        this.configurationBody = data.configurationBody;
        this.definitionHeader = data.definitionHeader;
        this.definitionBody = data.definitionBody;
        this.refreshAvailableColours();
    }

    refreshAvailableColours() {
        let cols = [];
        this.configurationBody
            .forEach((def: MatrixConfiguration[]) => {
                def.forEach(ch => !cols.includes(ch.Colour) ? cols.push(ch.Colour) : null);
            });
        this.availableColours = cols;
    }

    export() {
        let translateValues = this.translateMatrix();
        translateValues.subscribe({
            complete: () => {
                this.exportToJsonFile();
            }
        });
    }

    getTranslationsForRow(row: Array<MatrixDefinition | MatrixConfiguration>, key: string = this.sevMatrixKey): Observable<SimpleTranslation>[] {
        let translations: Observable<any>[] = [];

        row.forEach(column => {
            if (!column.description) {
                translations
                    .push(this.translationService.getTranslation(key + ':' + column.Alias, this.textProperty)
                        .pipe(map(result => {
                            column.description = result;
                            return {
                                listKey: key,
                                alias: column.Alias,
                                translation: column.description
                            }
                        })));
            } else {
                return translations.push(of({
                    listKey: key,
                    alias: column.Alias,
                    translation: column.description
                }));
            }
        });

        return translations;
    }

    translateMatrix() {
        let translations: Observable<SimpleTranslation>[] = [];

        translations = translations.concat(
            this.getTranslationsForRow(this.definitionHeader));
        translations = translations.concat(
            this.getTranslationsForRow(this.configurationHeader));
        this.configurationBody.forEach(row => {
            translations = translations.concat(
                this.getTranslationsForRow(row, this.sevMatrixDefaultKey));
        });
        this.definitionBody.forEach(row => {
            translations = translations.concat(
                this.getTranslationsForRow(row));
        });
        translations = translations.concat(this.translateTitles());
        if (translations.length) {
            return forkJoin(translations);
        } else {
            return EMPTY;
        }
    }

    private translateTitles(): Observable<SimpleTranslation>[] {
        let result: Observable<SimpleTranslation>[] = [];
        if (this.definitionTitle) {
            result.push(of({
                translation: this.definitionTitle,
                listKey: this.sevMatrixKey,
                alias: this.definitionTitleAlias
            }));
        } else {
            result.push(
                this.translationService.getTranslation(`${this.sevMatrixKey}:${this.definitionTitleAlias}`, this.textProperty)
                    .pipe(
                        map(translation => {
                            this.definitionTitle = translation;
                            let result: SimpleTranslation = {
                                alias: this.definitionTitleAlias,
                                listKey: this.sevMatrixKey,
                                translation: translation
                            };
                            return result;
                        })));
        }
        if (this.configurationTitle) {
            result.push(of({
                translation: this.configurationTitle,
                listKey: this.sevMatrixKey,
                alias: this.configurationTitleAlias
            }));
        } else {
            result.push(
                this.translationService.getTranslation(`${this.sevMatrixKey}:${this.configurationTitleAlias}`, this.textProperty)
                    .pipe(map(translation => {
                        this.configurationTitle = translation;
                        let result: SimpleTranslation = {
                            alias: this.configurationTitleAlias,
                            listKey: this.sevMatrixKey,
                            translation: translation
                        };
                        return result;
                    })));
        }

        return result;
    }

    convertCurrentToExportDefinition(): MatrixExportDefinition {
        return {
            configurationTitle: this.configurationTitle,
            configurationBody: this.configurationBody,
            configurationHeader: this.configurationHeader,
            definitionTitle: this.definitionTitle,
            definitionBody: this.definitionBody,
            definitionHeader: this.definitionHeader
        };
    }

    exportToJsonFile() {
        let exportDef: MatrixExportDefinition = this.convertCurrentToExportDefinition();
        let dataStr = JSON.stringify(exportDef);
        let dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        let exportFileDefaultName = 'matrix-configuration.json';

        let linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    }

    isSelected(col) {
        return col === this.selectedCell;
    }

    editTitle(titleKey: string, titleValue) {
        this.editingTitle = {
            key: titleKey,
            value: titleValue
        }
    }

    cancelEditing() {
        this.editingTitle = null;
        this.selectedCell = undefined;
        this.currentModel = undefined;
    }

    saveTitle(title: string) {
        switch (this.editingTitle.key) {
            case this.configurationTitleAlias:
                this.configurationTitle = title;
                break;
            case this.definitionTitleAlias:
                this.definitionTitle = title;
                break;
        }
        this.cancelEditing();
    }

    selected(key: string, row: MatrixConfigurationViewModel | MatrixDefinitionViewModel, useScoreAndColour: boolean = false) {
        this.cancelEditing();
        this.selectedCell = row;
        this.scoreAndColour = useScoreAndColour;
        if (row.description) {
            this.currentModel = row;
        } else {
            this.translationService.getTranslation(key + ':' + row.Alias, this.textProperty).subscribe(description => {
                row.description = description;
                this.currentModel = row;
            });
        }
    }

    publish() {
        this.tenantService.currentTenantStatus().pipe(take(1)).subscribe((status) => {
            if (status === 'LIVE') {
                this.toast.sendMessage("LIVE Tenant", "YOU ARE ABOUT TO UPDATE A LIVE TENANT", ToastMessageType.error, ToastButtons.OkCancel, 0)
                    .subscribe(response => response === ToastResponse.Ok ? this.confirmDoPublish() : null);
            } else {
                this.confirmDoPublish();
            }
        });
    }

    private confirmDoPublish() {
        let publishStatus = new Subject<SimpleMessage>();
        if (this.validate(false)) {
            this.toast.sendMessage('Publish To AVA', 'Are you sure? this will be updated immediately.', ToastMessageType.warning, ToastButtons.YesNo, 0, publishStatus)
                .subscribe((choice: ToastResponse) => {
                    if (choice === ToastResponse.Yes) {
                        this.doPublish(publishStatus)
                    }
                    else {
                        publishStatus.complete();
                    }
                });
        }
    }

    private doPublish(publishStatus: Subject<SimpleMessage>) {
        const tasks = {
            PublishMatrix: {
                include: true,
                content: { description: 'Publish Matrix', symbol: '►' }
            },
            PublishTranslation: {
                include: true,
                content: { description: 'Publish Translations', symbol: '►' }
            },
            UpdateQuery: {
                include: this.isSeverityMatrix,
                content: { description: 'Update Query', symbol: '►' }
            },
            RefreshTranslations: {
                include: true,
                content: { description: 'Refresh Act Translations', symbol: '►' }
            }
        };

        publishStatus.next({
            title: 'Please Wait',
            content: '',
            multiLineContent: Object.values(tasks).filter(v => v.include).map(v => v.content),
        });

        const observables = [
            this.matrixService.publishMatrix(this.newMatrixName, this.convertCurrentToExportDefinition())
                .pipe(tap(() => tasks.PublishMatrix.content.symbol = '✓')),
            this.translateMatrix()
                .pipe(tap(() => tasks.PublishTranslation.content.symbol = '✓'), mergeMap(result => this.translationService.pushTranslations(result))),
        ];
        if (this.isSeverityMatrix) {
            observables.push(this.matrixService.saveSeverityMatrixQuery(this.configurationBody, this.queryViewModel)
                .pipe(tap(() => tasks.UpdateQuery.content.symbol = '✓')));
        }

        forkJoin(observables)
            .pipe(tap(() => tasks.RefreshTranslations.content.symbol = '►'),
            mergeMap(() =>
                of(this.translationService.rebuildTranslationsForTenant(this.tenantService.selectedTenant.Value)).pipe(
                    tap(() => tasks.RefreshTranslations.content.symbol = '✓')
                ))
        ).subscribe(() => {
            publishStatus.complete();
            this.toast.sendMessage('Publish Success', 'This has been successfully published into AVA', ToastMessageType.success, ToastButtons.None);
            this.isCopy = false;
        });
    }

    swatchChanged($event: string) {
        this.savedColour = $event;
    }
}